import React from "react";
import { FilterState, Centre, ClassOption } from "./IReport";
import { Search, FileSpreadsheet } from "lucide-react";

interface FilterBarProps {
  filters: FilterState;
  onFilterChange: (key: keyof FilterState, value: string) => void;
  centres: Centre[];
  days: { label: string; value: string }[];
  classes: ClassOption[];
  onClear: () => void;
  onExcelDownload?: () => void;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  onFilterChange,
  centres,
  days,
  classes,
  onClear,
  onExcelDownload,
}) => {
  // Filter available classes based on selected centre and day (if any)
  // Also apply Tutor restrictions if applicable
  // const availableClasses = classes.filter((cls) => {
  //   const matchesCentre =
  //     filters.centreId === "" || cls.centreId === filters.centreId;
  //   const matchesDay =
  //     filters.dayOfWeek === "" || cls.dayOfWeek === filters.dayOfWeek;
  //   const matchesTutor =
  //     user.role === UserRole.ADMIN || user.assignedClassIds.includes(cls.id);
  //   return matchesCentre && matchesDay && matchesTutor;
  // });

  // Filter available centres based on Tutor role
  const availableCentres = centres;

  return (
    <div className="bg-white border-b border-slate-200 px-6 py-4 shadow-sm sticky top-0 z-20">
      {/* Top Row: Selectors and Search */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
        {/* Filters Group */}
        <div className="flex flex-wrap items-center gap-3 flex-1">
          {/* Centre Selector */}
          <div className="flex flex-col min-w-[140px]">
            <select
              value={filters.centreId}
              onChange={(e) => onFilterChange("centreId", e.target.value)}
              className="w-full bg-gray-50 border border-slate-300 text-slate-700 text-[12px] rounded-lg focus:ring-brand-500 focus:border-brand-500 p-2.5 appearance-none bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iOCIgdmlld0JveD0iMCAwIDEyIDgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik0xIDFMNiA2TDExIDEiIHN0cm9rZT0iIzQ5NDk0OSIgc3Ryb2tlLXdpZHRoPSIyIiBmaWxsPSJub25lIi8+Cjwvc3ZnPg==')] bg-no-repeat bg-[position:right_0.75rem_center] bg-[size:1rem_0.5rem] pr-8"
            >
              <option value="">All Centres</option>
              {availableCentres.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Day Selector */}
          <div className="flex flex-col min-w-[140px]">
            <select
              value={filters.dayOfWeek}
              onChange={(e) => onFilterChange("dayOfWeek", e.target.value)}
              className="w-full bg-gray-50 border border-slate-300 text-slate-700 text-[12px] rounded-lg focus:ring-brand-500 focus:border-brand-500 p-2.5 appearance-none bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iOCIgdmlld0JveD0iMCAwIDEyIDgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik0xIDFMNiA2TDExIDEiIHN0cm9rZT0iIzQ5NDk0OSIgc3Ryb2tlLXdpZHRoPSIyIiBmaWxsPSJub25lIi8+Cjwvc3ZnPg==')] bg-no-repeat bg-[position:right_0.75rem_center] bg-[size:1rem_0.5rem] pr-8"
            >
              <option value="">All Days</option>
              {days.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>

          {/* Class Selector */}
          <div className="flex flex-col min-w-[220px]">
            <select
              value={filters.classId}
              onChange={(e) => onFilterChange("classId", e.target.value)}
              className="w-full bg-gray-50 border border-slate-300 text-slate-700 text-[12px] rounded-lg focus:ring-brand-500 focus:border-brand-500 p-2.5 appearance-none bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iOCIgdmlld0JveD0iMCAwIDEyIDgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik0xIDFMNiA2TDExIDEiIHN0cm9rZT0iIzQ5NDk0OSIgc3Ryb2tlLXdpZHRoPSIyIiBmaWxsPSJub25lIi8+Cjwvc3ZnPg==')] bg-no-repeat bg-[position:right_0.75rem_center] bg-[size:1rem_0.5rem] pr-8"
              disabled={classes.length === 0}
            >
              <option value="">
                {classes.length === 0 ? "No classes found" : "All Classes"}
              </option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {`${c.name}`}
                </option>
              ))}
            </select>
          </div>

          {/* Clear Button */}
          <button
            onClick={onClear}
            className="px-4 py-2.5 text-[12px] text-[#0284c7] font-medium text-brand-600 bg-white border border-[#0284c7] border-brand-600 rounded-lg hover:bg-brand-50 focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-colors"
          >
            Clear
          </button>
        </div>

        {/* Right Side: Search & Export */}
        <div className="flex items-center gap-3 w-full lg:w-auto">
          <div className="relative w-full lg:w-74">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search className="w-[12px] h-[12px] text-gray-500" />
            </div>
            <input
              type="text"
              className="block w-full p-2.5 pl-10 text-[12px] text-slate-900 border border-slate-300 rounded-lg bg-gray-50 focus:ring-brand-500 focus:border-brand-500"
              placeholder="Search history..."
              value={filters.searchQuery}
              onChange={(e) => onFilterChange("searchQuery", e.target.value)}
            />
          </div>

          <button
            onClick={onExcelDownload}
            className="p-2 text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
            title="Export to Excel"
          >
            <FileSpreadsheet className="w-[20px] h-[20px]" />
          </button>
        </div>
      </div>

      {/* Breadcrumb / Context Line */}
      <div className="mt-[12px] text-[12px] text-slate-500 flex items-center gap-1">
        <span>Showing print history for:</span>
        <span className="font-medium text-slate-700">
          {filters.centreId
            ? availableCentres.find((c) => c.id === filters.centreId)?.name
            : "All Centres"}
        </span>
        <span>•</span>
        <span className="font-medium text-slate-700">
          {filters.dayOfWeek ? filters.dayOfWeek : "All Days"}
        </span>
        <span>•</span>
        <span className="font-medium text-slate-700">
          {filters.classId
            ? classes.find((c) => c.id === filters.classId)?.name
            : "All Classes"}
        </span>
      </div>
    </div>
  );
};
